import { useState } from 'react'
import {
  CURRENCIES,
  PLAN_PRICES,
  formatPrice,
  getStoredCurrency,
  setStoredCurrency,
  type CurrencyCode,
} from '../lib/currency'
import { PAYMENT_CONFIG, whatsappContactUrl } from '../lib/payments'

export default function LandingPage({
  onLogin,
  onRegister,
}: {
  onLogin: () => void
  onRegister: () => void
}) {
  const [currency, setCurrency] = useState<CurrencyCode>(getStoredCurrency())

  const changeCurrency = (code: CurrencyCode) => {
    setCurrency(code)
    setStoredCurrency(code)
  }
  const features = [
    {
      icon: '🛡️',
      title: 'Filtros Web Inteligentes',
      description: 'Bloquea contenido inapropiado por categorías y rangos de edad en Windows y Android.',
    },
    {
      icon: '⏱️',
      title: 'Control de Tiempo de Pantalla',
      description: 'Límites diarios por dispositivo y bloqueo remoto instantáneo desde el panel.',
    },
    {
      icon: '📍',
      title: 'Ubicación en Tiempo Real',
      description: 'Geolocalización GPS del móvil de tu hijo con geocercas y alertas.',
    },
    {
      icon: '📱',
      title: 'Gestión de Apps',
      description: 'Ve las apps instaladas, el tiempo de uso y bloquea las que quieras con un clic.',
    },
    {
      icon: '📞',
      title: 'Llamadas, SMS y Contactos',
      description: 'Historial de llamadas, mensajes y contactos del dispositivo de tu hijo.',
    },
    {
      icon: '📊',
      title: 'Reportes de Actividad',
      description: 'Gráficos de uso por app, dispositivo en línea y alertas en tiempo real.',
    },
  ]

  const plans: {
    name: string
    price: string
    period: string
    features: string[]
    cta: string
    popular: boolean
    ctaHref?: string
  }[] = [
    {
      name: 'Prueba Gratis',
      price: 'Gratis',
      period: ' / 14 días',
      features: [
        'Todas las funciones',
        'Windows + Android',
        'Panel web completo',
        'Sin tarjeta de crédito',
      ],
      cta: 'Empezar gratis',
      popular: false,
    },
    {
      name: 'Premium',
      price: formatPrice(PLAN_PRICES.premium[currency], currency),
      period: ' / mes',
      features: [
        'Hasta 4 dispositivos',
        'Filtros web por edad',
        'Reportes avanzados',
        'Soporte prioritario',
      ],
      cta: 'Empezar prueba 14 días',
      popular: true,
    },
    {
      name: 'Familia',
      price: formatPrice(PLAN_PRICES.familia[currency], currency),
      period: ' / mes',
      features: [
        'Hasta 10 dispositivos',
        'Panel familiar completo',
        'Soporte 24/7',
        'Actualizaciones incluidas',
      ],
      cta: 'Hablar por WhatsApp',
      ctaHref: whatsappContactUrl(
        'Hola, quiero el plan Familia de KidSafe (10 dispositivos). ¿Me ayudas a activarlo?',
        PAYMENT_CONFIG.whatsappNumber,
      ),
      popular: false,
    },
  ]

  const faqs = [
    {
      q: '¿Mi hijo sabrá que está protegido?',
      a: 'KidSafe muestra un icono visible y pantallas claras cuando bloquea algo. Recomendamos hablarlo en familia: la app es una herramienta de cuidado, no de espionaje.',
    },
    {
      q: '¿En qué dispositivos funciona?',
      a: 'En celulares y tablets Android, y en computadores con Windows. Cada plan incluye un número de dispositivos que puedes gestionar desde un solo panel.',
    },
    {
      q: '¿Necesito la tarjeta de crédito para probar?',
      a: 'No. Los 14 días de prueba son gratis y sin tarjeta. Solo pagas si decides continuar con Premium o Familia.',
    },
    {
      q: '¿Qué pasa cuando terminan los 14 días gratis?',
      a: 'Te avisamos antes del vencimiento. Si no activas un plan, la protección se pausa hasta que renueves: tus datos y dispositivos se conservan.',
    },
    {
      q: '¿Cómo pago la suscripción?',
      a: 'Con tarjeta, PSE, Nequi o transferencia desde el panel (Mi cuenta → Suscripción). Subes el comprobante y activamos tu plan en minutos.',
    },
    {
      q: '¿Puedo cancelar cuando quiera?',
      a: 'Sí, no hay permanencia ni penalidades. Escríbenos por WhatsApp y gestionamos tu cancelación de inmediato.',
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛡️</span>
            <span className="text-xl font-bold text-brand-700">KidSafe</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onLogin}
              className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Iniciar sesión
            </button>
            <button
              onClick={onRegister}
              className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Crear cuenta gratis
            </button>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-brand-50 to-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="inline-block rounded-full bg-brand-100 px-3 py-1 text-xs font-bold text-brand-700">
              PRUEBA GRATIS · 14 DÍAS · SIN TARJETA
            </span>
            <h1 className="mt-4 text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
              Protege a tus hijos en el mundo digital
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-600">
              Control parental completo para <b>Windows y Android</b>: filtros web por edad, tiempo de
              pantalla, ubicación en tiempo real y reportes de actividad — todo desde un solo panel.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={onRegister}
                className="rounded-xl bg-brand-600 px-8 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                Crear cuenta gratis
              </button>
              <button
                onClick={onLogin}
                className="rounded-xl border border-slate-300 bg-white px-8 py-3.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                Ya tengo cuenta
              </button>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-500">
              <span>🔒 Acceso privado con tu cuenta</span>
              <span>✅ Sin anuncios</span>
              <span>⚡ Instalación en 2 minutos</span>
            </div>
          </div>

          <div className="relative">
            <div className="mx-auto max-w-md rounded-3xl bg-slate-900 p-3 shadow-2xl">
              <div className="rounded-2xl bg-slate-800 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-200">KidSafe · Panel</span>
                  <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> En línea
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-slate-700/50 p-3 text-center">
                    <p className="text-lg font-bold text-white">2h 15m</p>
                    <p className="text-[11px] text-slate-400">Hoy</p>
                  </div>
                  <div className="rounded-xl bg-slate-700/50 p-3 text-center">
                    <p className="text-lg font-bold text-white">3</p>
                    <p className="text-[11px] text-slate-400">Bloqueadas</p>
                  </div>
                  <div className="rounded-xl bg-slate-700/50 p-3 text-center">
                    <p className="text-lg font-bold">📍</p>
                    <p className="text-[11px] text-slate-400">Casa</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-xl bg-slate-700/50 px-3 py-2.5">
                    <span className="text-sm text-slate-300">🎮 Juegos</span>
                    <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-xs text-rose-400">Bloqueado</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-700/50 px-3 py-2.5">
                    <span className="text-sm text-slate-300">🌐 Navegador</span>
                    <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-400">Permitido</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-700/50 px-3 py-2.5">
                    <span className="text-sm text-slate-300">💬 Redes sociales</span>
                    <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-xs text-rose-400">Bloqueado</span>
                  </div>
                </div>
                <div className="mt-4 flex h-20 items-end gap-1.5 rounded-xl bg-slate-700/50 p-3">
                  {[40, 65, 30, 80, 55, 90, 45].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-brand-500"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
              Todo lo que necesitas para protegerlos
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Herramientas diseñadas para padres modernos, en Windows y Android
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <article
                key={f.title}
                className="rounded-2xl bg-white p-6 ring-1 ring-slate-200 transition hover:shadow-lg"
              >
                <div className="text-4xl">{f.icon}</div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works / Downloads teaser */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900">Instálalo en minutos</h2>
          <p className="mt-3 text-lg text-slate-600">
            Crea tu cuenta y descarga el agente para cada dispositivo de tus hijos
          </p>
          <div className="mx-auto mt-12 grid max-w-4xl gap-8 md:grid-cols-3">
            {[
              { n: '1', t: 'Crea tu cuenta', d: 'Registro gratuito en menos de 2 minutos' },
              { n: '2', t: 'Descarga el agente', d: 'Instaladores para Windows y Android disponibles en tu panel' },
              { n: '3', t: 'Configura la protección', d: 'Filtros por edad, límites de tiempo y bloqueo de apps' },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-xl font-bold text-white">
                  {s.n}
                </div>
                <h3 className="mt-4 font-semibold text-slate-800">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
              Planes flexibles para cada familia
            </h2>
            <p className="mt-3 text-lg text-slate-600">Empieza gratis, actualiza cuando quieras</p>
            <p className="mt-2 text-sm text-slate-500">
              Sin tarjeta de crédito · Cancela cuando quieras · Soporte por WhatsApp
            </p>
            <div className="mt-6 inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              {(Object.keys(CURRENCIES) as CurrencyCode[]).map((code) => (
                <button
                  key={code}
                  onClick={() => changeCurrency(code)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    currency === code
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {CURRENCIES[code].flag} {code}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <article
                key={plan.name}
                className={`relative flex flex-col rounded-2xl bg-white p-8 shadow-sm ring-1 ${
                  plan.popular ? 'ring-2 ring-brand-500 shadow-xl' : 'ring-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-bold text-white">
                    MÁS POPULAR
                  </div>
                )}
                <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="pb-1 text-slate-500">{plan.period}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="font-bold text-emerald-500">✓</span> {f}
                    </li>
                  ))}
                </ul>
                {plan.ctaHref ? (
                  <a
                    href={plan.ctaHref}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-8 block w-full rounded-xl border-2 border-emerald-600 bg-emerald-50 py-3 text-center font-semibold text-emerald-700 transition hover:bg-emerald-100"
                  >
                    💬 {plan.cta}
                  </a>
                ) : (
                  <button
                    onClick={onRegister}
                    className={`mt-8 w-full rounded-xl py-3 font-semibold transition ${
                      plan.popular
                        ? 'bg-brand-600 text-white hover:bg-brand-700'
                        : 'border-2 border-brand-600 text-brand-600 hover:bg-brand-50'
                    }`}
                  >
                    {plan.cta}
                  </button>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
              Preguntas frecuentes
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Lo que más nos preguntan los padres antes de empezar
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-white p-5 ring-1 ring-slate-200 transition hover:shadow-md"
              >
                <summary className="cursor-pointer list-none font-semibold text-slate-800 transition hover:text-brand-700">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-500">
            ¿Otra duda?{' '}
            <a
              href={whatsappContactUrl('Hola, tengo una duda sobre KidSafe.', PAYMENT_CONFIG.whatsappNumber)}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-emerald-600 hover:underline"
            >
              Escríbenos por WhatsApp 💬
            </a>
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-600 py-16">
        <div className="mx-auto max-w-2xl px-4 text-center">
          <h2 className="text-3xl font-bold text-white">¿Listo para empezar?</h2>
          <p className="mt-2 text-brand-100">
            Crea tu cuenta en menos de 2 minutos. Sin tarjeta de crédito.
          </p>
          <button
            onClick={onRegister}
            className="mt-6 rounded-xl bg-white px-8 py-3.5 text-lg font-semibold text-brand-700 shadow hover:bg-brand-50"
          >
            Crear cuenta gratis
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 text-slate-400">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🛡️</span>
              <span className="text-lg font-bold text-white">KidSafe</span>
            </div>
            <p className="text-sm">Protección digital para familias modernas</p>
            <div className="flex gap-6 text-sm">
              <button onClick={onLogin} className="hover:text-white">Iniciar sesión</button>
              <button onClick={onRegister} className="hover:text-white">Crear cuenta</button>
            </div>
          </div>
          <div className="mt-8 border-t border-slate-800 pt-6 text-center text-sm">
            © 2026 KidSafe. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  )
}